import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./stores/auth";
import { PageLoader, Toasts } from "./components/ui";
import { Layout } from "./components/Layout";
import { RequireModule } from "./components/RequireModule";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Orders } from "./pages/Orders";
import { Fulfilment } from "./pages/Fulfilment";
import { PickListDetail } from "./pages/PickListDetail";
import { PackingBoxes } from "./pages/PackingBoxes";
import { Receipts } from "./pages/Receipts";
import { Products } from "./pages/Products";
import { ShopListings } from "./pages/ShopListings";
import { ProductTypes } from "./pages/ProductTypes";
import { Categories } from "./pages/Categories";
import { Inventory } from "./pages/Inventory";
import { Discounts } from "./pages/Discounts";
import { Shipping } from "./pages/Shipping";
import { CardUploader } from "./pages/CardUploader";
import { Raffles } from "./pages/Raffles";
import { Auctions } from "./pages/Auctions";
import { Packs } from "./pages/Packs";
import { MarketplaceSellers } from "./pages/MarketplaceSellers";
import { MarketplaceListings } from "./pages/MarketplaceListings";
import { MarketplaceSales } from "./pages/MarketplaceSales";
import { FlexxiTemplates } from "./pages/FlexxiTemplates";
import { FlexxiPlans } from "./pages/FlexxiPlans";
import { FlexxiReputation } from "./pages/FlexxiReputation";
import { CommerceVerifications } from "./pages/commerce/CommerceVerifications";
import { Users } from "./pages/Users";
import { Cases } from "./pages/Cases";
import { AiModels } from "./pages/AiModels";
import { AiForms } from "./pages/AiForms";
import { Settings } from "./pages/Settings";
import { Roles } from "./pages/Roles";
import { Modules } from "./pages/Modules";
import { Audit } from "./pages/Audit";
import { DocumentTemplates } from "./pages/DocumentTemplates";
import { CmsPages } from "./pages/cms/CmsPages";
import { CmsPageEditor } from "./pages/cms/CmsPageEditor";
import { CmsHero } from "./pages/cms/CmsHero";
import { ProfileSecurity } from "./pages/ProfileSecurity";
import { Notifications } from "./pages/Notifications";
import { OsMode } from "./pages/OsMode";
import { AdminConfigLayout } from "./pages/admin/AdminConfigLayout";
import { AdminIndexRedirect, LegacyAdminRedirect, LegacyCmsRedirect } from "./pages/admin/AdminRedirects";
import { CommerceLegacyRedirect } from "./pages/commerce/CommerceLegacyRedirect";
import { CommerceSettings } from "./pages/commerce/CommerceSettings";
import { FlexxiModuleLayout } from "./pages/flexxi/FlexxiModuleLayout";
import { FlexxiIndexRedirect } from "./pages/flexxi/FlexxiIndexRedirect";
import { MarketplaceModuleLayout } from "./pages/marketplace/MarketplaceModuleLayout";
import { MarketplaceIndexRedirect } from "./pages/marketplace/MarketplaceIndexRedirect";

function RequireStaff({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  const isStaff = user && (user.isSuperadmin || user.permissions.length > 0);
  if (!isStaff) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}

export default function App() {
  const bootstrap = useAuth((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <>
            <Login />
            <Toasts />
          </>
        }
      />
      <Route
        path="/os"
        element={
          <RequireStaff>
            <OsMode />
            <Toasts />
          </RequireStaff>
        }
      />
      <Route
        element={
          <RequireStaff>
            <Layout />
          </RequireStaff>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/fulfilment" element={<Fulfilment />} />
        <Route path="/fulfilment/picklists/:id" element={<PickListDetail />} />
        <Route path="/packing-boxes" element={<PackingBoxes />} />
        <Route path="/receipts" element={<Receipts />} />
        <Route path="/products" element={<Products />} />
        <Route path="/shop-listings" element={<RequireModule module="ecommerce"><ShopListings /></RequireModule>} />
        <Route path="/product-types" element={<ProductTypes />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/discounts" element={<Discounts />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/card-uploader" element={<RequireModule module="card_uploader"><CardUploader /></RequireModule>} />
        <Route path="/raffles" element={<RequireModule module="raffles"><Raffles /></RequireModule>} />
        <Route path="/auctions" element={<RequireModule module="auctions"><Auctions /></RequireModule>} />
        <Route path="/packs" element={<RequireModule module="packs"><Packs /></RequireModule>} />
        <Route path="/marketplace/orders" element={<Navigate to="/marketplace/sales" replace />} />
        <Route path="/commerce/verifications" element={<CommerceVerifications />} />
        <Route path="/commerce/*" element={<CommerceLegacyRedirect />} />
        <Route path="/marketplace" element={<MarketplaceModuleLayout />}>
          <Route index element={<MarketplaceIndexRedirect />} />
          <Route
            path="listings"
            element={
              <RequireModule module="marketplace">
                <MarketplaceListings />
              </RequireModule>
            }
          />
          <Route
            path="sales"
            element={
              <RequireModule module="marketplace">
                <MarketplaceSales />
              </RequireModule>
            }
          />
          <Route
            path="sellers"
            element={
              <RequireModule module="marketplace">
                <MarketplaceSellers />
              </RequireModule>
            }
          />
          <Route
            path="settings"
            element={
              <RequireModule module="marketplace">
                <CommerceSettings tab="marketplace" />
              </RequireModule>
            }
          />
        </Route>
        <Route path="/flexxi" element={<FlexxiModuleLayout />}>
          <Route index element={<FlexxiIndexRedirect />} />
          <Route
            path="templates"
            element={
              <RequireModule module="instalments">
                <FlexxiTemplates />
              </RequireModule>
            }
          />
          <Route
            path="plans"
            element={
              <RequireModule module="instalments">
                <FlexxiPlans />
              </RequireModule>
            }
          />
          <Route
            path="reputation"
            element={
              <RequireModule module="instalments">
                <FlexxiReputation />
              </RequireModule>
            }
          />
          <Route
            path="verifications"
            element={<Navigate to="/commerce/verifications?tab=flexxi" replace />}
          />
          <Route
            path="settings"
            element={
              <RequireModule module="instalments">
                <CommerceSettings tab="flexxi" />
              </RequireModule>
            }
          />
        </Route>
        <Route path="/users" element={<Users />} />
        <Route path="/cases" element={<RequireModule module="support_forms"><Cases /></RequireModule>} />
        <Route path="/content/pages" element={<RequireModule module="cms"><CmsPages /></RequireModule>} />
        <Route path="/content/pages/:id" element={<RequireModule module="cms"><CmsPageEditor /></RequireModule>} />
        <Route path="/content/slider" element={<RequireModule module="cms"><CmsHero /></RequireModule>} />
        <Route path="/admin/cms/*" element={<LegacyCmsRedirect />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile/security" element={<ProfileSecurity />} />
        <Route path="/ai/models" element={<LegacyAdminRedirect to="/admin/ai/models" />} />
        <Route path="/ai/forms" element={<LegacyAdminRedirect to="/admin/support/forms" />} />
        <Route path="/settings" element={<LegacyAdminRedirect to="/admin/settings/general" />} />
        <Route path="/roles" element={<LegacyAdminRedirect to="/admin/roles" />} />
        <Route path="/modules" element={<LegacyAdminRedirect to="/admin/modules" />} />
        <Route path="/audit" element={<LegacyAdminRedirect to="/admin/audit" />} />
        <Route path="/admin" element={<AdminConfigLayout />}>
          <Route index element={<AdminIndexRedirect />} />
          <Route path="settings" element={<Navigate to="general" replace />} />
          <Route path="settings/shipping" element={<LegacyAdminRedirect to="/shipping" />} />
          <Route path="settings/marketplace" element={<LegacyAdminRedirect to="/marketplace/settings" />} />
          <Route path="settings/flexxi" element={<LegacyAdminRedirect to="/flexxi/settings" />} />
          <Route path="settings/:section" element={<Settings />} />
          <Route path="ai/models" element={<RequireModule module="ai_copilot_bo"><AiModels /></RequireModule>} />
          <Route path="ai/forms" element={<LegacyAdminRedirect to="/admin/support/forms" />} />
          <Route path="support/forms" element={<RequireModule module="support_forms"><AiForms /></RequireModule>} />
          <Route path="roles" element={<Roles />} />
          <Route path="modules" element={<Modules />} />
          <Route path="audit" element={<Audit />} />
          <Route path="document-templates" element={<RequireModule module="documents"><DocumentTemplates /></RequireModule>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
